/* eslint-disable */
// @ts-nocheck
import * as R from 'ramda';
import type { CohortResponses } from '../../../../main/types/druidTypes';
import type { lineSeriesType } from '../../../../main/types/timeseriesTypes';
import { createMomentObjInTimezone, isMomentBridged } from '../../../../main/support/dateUtils';
import { ADS_EVENTS_HUMAN_READABLE } from '../../eo_mapping';
import { COHORT_CALCULATED_METRICS } from '../../eo_mapping/calculated_properties';
import { UNDISCLOSED_EVENT_VALUE } from '../eo_constants';

const generateCommaSeparated = (measure: string, dimensions: Array<string>, record: Record<string, any>) => {
  return `${measure}${dimensions.length > 0 ? ', ' : ''}${dimensions
    .map((dim) => (!!record[dim] ? record[dim] : '--'))
    .join(', ')}`;
};

const formatTimeseriesYValue = (value?: number) => {
  return value === UNDISCLOSED_EVENT_VALUE ? UNDISCLOSED_EVENT_VALUE : +value;
};

const formatDataForTimeseries = (
  data: CohortResponses,
  measure: string,
  dimensions: Array<string>,
  bandCount: number
): lineSeriesType => {
  return data.reduce((acc, result) => {
    if (result.measure !== measure) {
      return acc;
    }

    let bandData = result.data || [];
    bandData = bandData.slice(0, bandCount);
    const lineSeries = generateCommaSeparated(measure, dimensions, result);
    return R.assoc(
      lineSeries,
      {
        data: bandData.map((datum, index) => ({
          x: index,
          y: formatTimeseriesYValue(datum),
        })),
      },
      acc
    );
  }, {});
};

export const mapDataToTimeseries = (
  data: CohortResponses,
  bandCount: number,
  cohortEventTypes: Array<string>,
  dimensions: Array<string>
): Array<lineSeriesType> => {
  return cohortEventTypes.map((measure) => {
    return formatDataForTimeseries(data, measure, dimensions, bandCount);
  });
};

/**
 * converts a series of offsets from a given day (represented as an array of numbers) to an array of moment-timezone objects that represent last day in a cohort band.
 * For example: Say the cohort time range chosen is 8/5 - 8/10.
 * Day 0 would encompass data collected on 8/6, 8/7, 8/8 ... 8/11. 8/10 would be returned.
 * Week 0 would encompass data collected on 8/6, 8/7, 8/8 ... 8/17. 8/17 would be returned.
 * Month 0 would encompass data collected on 8/6 ... 9/9 (30 days from 8/10). 9/9 would be returned.
 */
export const convertBandToLastMoment = (offset: number, base: string, step: number) => {
  const baseAsMoment = createMomentObjInTimezone(base);
  return baseAsMoment.clone().add(step * offset, 'days');
};
export const remapCohortDimension = (dimension: string, cohortType: string): string => {
  return cohortType.includes('install') ? `install_${dimension}` : `reengagement_${dimension}`;
};
export const remapCohortDimensions = (
  dimensions: Array<string>,
  dataSource: string,
  dimensionsToRemap: Array<string>
): Array<string> => {
  return dimensions.map((dimension) => {
    const dimensionToRemap = R.find((dim) => dimension.includes(dim), dimensionsToRemap);

    if (dimensionToRemap) {
      return remapCohortDimension(dimensionToRemap, dataSource);
    }

    return dimension;
  });
};

/**
 * Remaps all dimensions from install to reengagement or vice versa
 *   based on dataSource passed in
 */
export const remapCohortFilters = (
  filter: Record<string, any>,
  dataSource: string,
  dimensionsToRemap: Array<string>
): Record<string, any> => {
  if (!filter) {
    return filter;
  }

  if (filter.dimension) {
    const dimensionToRemap = R.find((dim) => filter.dimension.includes(dim), dimensionsToRemap);
    return {
      ...filter,
      dimension: dimensionToRemap ? remapCohortDimension(dimensionToRemap, dataSource) : filter.dimension,
    };
  }

  if (filter.field) {
    return { ...filter, field: remapCohortFilters(filter.field, dataSource, dimensionsToRemap) };
  }

  if (filter.fields) {
    return { ...filter, fields: filter.fields.map((field) => remapCohortFilters(field, dataSource, dimensionsToRemap)) };
  }

  return filter;
};
export const generateCohortTitle = (
  cumulative: boolean,
  perUser: boolean,
  cohortEventTypes: Array<string>,
  granularity: string,
  costPerEvent: boolean,
  calculationMap: Record<string, any>
): string => {
  return cohortEventTypes
    .map((event) => {
      const calculatedMetricTitle = ADS_EVENTS_HUMAN_READABLE[event];
      return (
        // NOTE: undefined checks are used here since we are checking for the existence of these parameters not whether they are true or false.
        // If they exist we want to represent that the cohort menu buttons have no affect on the calculation required to arrive at the measure value.
        `${
          cumulative && (!calculationMap[event] || calculationMap[event].cumulative === undefined) ? 'Cumulative' : ''
        }${' '}${
          costPerEvent && (!calculationMap[event] || calculationMap[event].cost_per_event === undefined) ? 'Cost per' : ''
        }${' '}${calculatedMetricTitle || event || ''}${' '}${
          perUser && (!calculationMap[event] || calculationMap[event].per_user === undefined) ? 'per User' : ''
        }${' by '}${granularity || ''}`
      );
    })
    .join(' and ');
};
export const hasDateCompareBy = (dimensions: Array<string>) => {
  if (!dimensions) {
    return undefined;
  }

  return R.find((dim) => dim.includes('activity_timezone_adjusted_day'), dimensions);
};
// INTENG-5372: Due to druid unique count approximation errors, there is a chance that retention values may be greater than 1.
// To hedge against this risk, let's round these values down to 1.
export const handleRetention = (responses?: CohortResponses | null | undefined) => {
  if (!responses || !responses.length) {
    return [];
  }

  return responses.map((response) => {
    const bandData = response.data || [];
    return R.assoc(
      'data',
      bandData.map((datum) => (datum > 1 ? 1 : datum)),
      response
    );
  });
};

/**
 * Most calculated queries rely on cost being checked
 * The logic is as follows:
 * - A currently checked cost checkbox should always remain checked
 * - Check cost if any of the measures are calculated metrics
      - except measures listed in COST_CHECKBOX_WHITELIST
 *
 * COST_CHECKBOX_WHITELIST - measures in COHORT_CALCULATED_METRICS that dont't need cost checked
 */
const COST_CHECKBOX_WHITELIST = ['Retention', 'LTV', 'revenue'];
export const shouldCheckCostCheckbox = (measures: Array<string>, current: boolean) => {
  return measures.reduce((acc, event) => {
    return acc || (COHORT_CALCULATED_METRICS[event] && !COST_CHECKBOX_WHITELIST.includes(event));
  }, current);
};

/**
 * Most calculated queries should not have unique checked.
 * The logic is as follows:
 * - A currently unique checkbox should never be automatically checked
 * - Uncheck unique for all calculated measures (except measures listed in UNIQUE_CHECKBOX_WHITELIST
 * - Else, retain current checked state
 *
 * * UNIQUE_CHECKBOX_WHITELIST - measures in COHORT_CALCULATED_METRICS that can have unique checked
 */
const UNIQUE_CHECKBOX_WHITELIST = ['Retention', 'revenue'];
export const shouldCheckUniqueCheckbox = (measures: Array<string>, current: boolean) => {
  return measures.reduce((acc, event) => {
    // Don't set unique for non-whitelisted calculated metrics
    if (COHORT_CALCULATED_METRICS[event] && !UNIQUE_CHECKBOX_WHITELIST.includes(event)) {
      return false;
    }

    // Return current checked value
    return acc;
  }, current);
};

/**
 * Method that returns whether the hll sketch uniques cutoff date is bridged.
 */
export const isUniquesCutoffBridged = (startDate: string, endDate: string) => {
  const { UNIQUES_CUTOFF_DATE } = window;
  const { hll_sketch_uniques } = window.App.context.flipper;
  return hll_sketch_uniques && isMomentBridged(UNIQUES_CUTOFF_DATE, startDate, endDate);
};

/**
 * method that takes a cohort response and converts all user values to '--'.
 */
export const sanitizeUsers = (data: CohortResponses) => {
  return data.map((datum) => R.assoc('users', '--', datum));
};

/**
 * Method that sanitizes user data if the hll sketch uniques cutoff date is bridged.
 */
export const handleUniquesCutoff = (data: CohortResponses, startDate: string, endDate: string) => {
  if (!isUniquesCutoffBridged(startDate, endDate)) {
    return data;
  }

  return sanitizeUsers(data);
};
